import React from "react";
import { ArrowRight } from "lucide-react";
import { Section, Kicker, Heading, Rule } from "../components/ui";
import Reveal from "../components/Reveal";

const FLOW = ["Acquisition", "Integration", "Stewardship", "Long-term hold"];

const About = () => {
  return (
    <>
      {/* Hero / mission */}
      <section className="bg-navy text-white pt-36 pb-24 lg:pt-44 lg:pb-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <Kicker light>About OTP Group</Kicker>
            <Heading as="h1" className="text-3xl md:text-5xl lg:text-6xl max-w-4xl leading-[1.15]">
              Building a lasting group through acquisition and stewardship.
            </Heading>
          </Reveal>
        </div>
      </section>

      {/* Our approach */}
      <Section>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <Reveal>
              <Kicker>Our approach</Kicker>
              <Heading className="text-3xl md:text-4xl">A disciplined holding model.</Heading>
              <Rule className="mt-6" />
            </Reveal>
          </div>
          <div className="lg:col-span-8 space-y-6 text-lg text-steel leading-relaxed">
            <Reveal delay={0.05}>
              <p>
                OTP Group is a privately owned holding company focused on the UK residential property
                sector. We acquire established letting and estate agencies and residential property
                portfolios, and we hold them for the long term.
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <p>
                Our emphasis is on continuity and stewardship, retaining the teams, clients and
                local reputation that make a business valuable, rather than on short-term trading or
                rapid resale.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <p>
                We grow through arranged debt and disciplined reinvestment rather than external
                investment. This keeps ownership stable and allows us to make decisions with a
                genuinely long time horizon.
              </p>
            </Reveal>
          </div>
        </div>
      </Section>

      {/* Our model (lender credibility) */}
      <Section stone>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <Reveal>
              <Kicker>Our model</Kicker>
              <Heading className="text-3xl md:text-4xl">Built for lenders and partners.</Heading>
              <Rule className="mt-6" />
              <p className="mt-6 text-steel leading-relaxed max-w-md">
                For lenders and funding partners, OTP Group offers a clear and consistent structure:
                established, cash-generative businesses acquired on sensible terms, integrated
                carefully, and held for the long term. We value straightforward, transparent
                relationships with our funding partners.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              {/* Simple acquisition-to-integration flow */}
              <div className="flex flex-col sm:flex-row items-stretch gap-3">
                {FLOW.map((step, i) => (
                  <React.Fragment key={step}>
                    <div className="flex-1 border border-navy/15 bg-stone p-6 text-center">
                      <div className="text-xs tracking-kicker uppercase text-steel mb-2">
                        Step {i + 1}
                      </div>
                      <div className="font-semibold tracking-heading text-navy">{step}</div>
                    </div>
                    {i < FLOW.length - 1 && (
                      <div className="hidden sm:flex items-center text-steel">
                        <ArrowRight size={18} />
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </Section>
    </>
  );
};

export default About;
